// Capture Google Ads attribution data from URL parameters
// Stores "first" (first touch) and "latest" (most recent) values

const STORAGE_KEY_PREFIX = 'bc_attribution_'

interface AttributionData {
  firstGoogleClickId: string
  firstGoogleClientId: string
  latestGoogleClickId: string
  latestGoogleClientId: string
  utmSource: string
  utmMedium: string
  utmCampaign: string
  utmContent: string
  utmKeyword: string
  utmMatchtype: string
  campaignId: string
  adGroupId: string
  adId: string
}

function getParam(key: string): string {
  const params = new URLSearchParams(window.location.search)
  return params.get(key) || ''
}

function getGoogleClientId(): string {
  // Try to get from GA cookie (_ga)
  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_ga='))
    if (cookie) {
      // _ga cookie format: GA1.2.XXXXXXXXXX.XXXXXXXXXX
      const parts = cookie.split('.')
      if (parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`
      }
    }
  } catch (e) {
    // ignore
  }

  // Try gtag API
  try {
    const w = window as any
    if (w.gaGlobal && w.gaGlobal.vid) {
      return w.gaGlobal.vid
    }
  } catch (e) {
    // ignore
  }

  return ''
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + key, value)
  } catch (e) {
    // localStorage unavailable
  }
}

function retrieve(key: string): string {
  try {
    return localStorage.getItem(STORAGE_KEY_PREFIX + key) || ''
  } catch (e) {
    return ''
  }
}

/**
 * Call this on page load to capture URL params and store attribution.
 * First-touch values are only set once. Latest values are updated every visit.
 */
export function captureAttribution(): void {
  const gclid = getParam('gclid')
  const clientId = getGoogleClientId()

  // Always update "latest" if we have a value
  if (gclid) {
    store('latestGoogleClickId', gclid)
    // Set first only if not already set
    if (!retrieve('firstGoogleClickId')) {
      store('firstGoogleClickId', gclid)
    }
  }

  if (clientId) {
    store('latestGoogleClientId', clientId)
    if (!retrieve('firstGoogleClientId')) {
      store('firstGoogleClientId', clientId)
    }
  }

  // Store UTM params (latest always, first only once)
  const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_keyword',
    'utm_matchtype',
    'campaign_id',
    'ad_group_id',
    'ad_id',
  ]

  utmKeys.forEach((key) => {
    const value = getParam(key)
    if (value) {
      store(`latest_${key}`, value)
      if (!retrieve(`first_${key}`)) {
        store(`first_${key}`, value)
      }
    }
  })
}

/**
 * Returns all stored attribution data for the webhook payload.
 */
export function getAttributionData(): AttributionData {
  // Try to get client ID again in case it wasn't available on first load
  const freshClientId = getGoogleClientId()
  if (freshClientId) {
    store('latestGoogleClientId', freshClientId)
    if (!retrieve('firstGoogleClientId')) {
      store('firstGoogleClientId', freshClientId)
    }
  }

  return {
    firstGoogleClickId: retrieve('firstGoogleClickId'),
    firstGoogleClientId: retrieve('firstGoogleClientId'),
    latestGoogleClickId: retrieve('latestGoogleClickId'),
    latestGoogleClientId: retrieve('latestGoogleClientId'),
    utmSource: retrieve('latest_utm_source'),
    utmMedium: retrieve('latest_utm_medium'),
    utmCampaign: retrieve('latest_utm_campaign'),
    utmContent: retrieve('latest_utm_content'),
    utmKeyword: retrieve('latest_utm_keyword'),
    utmMatchtype: retrieve('latest_utm_matchtype'),
    campaignId: retrieve('latest_campaign_id'),
    adGroupId: retrieve('latest_ad_group_id'),
    adId: retrieve('latest_ad_id'),
  }
}
